// src/components/chat/MessageList.tsx
// Container for displaying chat messages with streaming support

import React, { useEffect, useRef, useState } from 'react';
import {
  Bot,
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  CircleStop,
} from 'lucide-react';
import MessageBubble from './MessageBubble';
import SegmentRenderer from './SegmentRenderer';
import type { ChatMessage, MessageSegment, ArtifactInfo } from '@/types/chat';

interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  isTyping?: boolean;
  liveSegments?: MessageSegment[];
  thinkingText?: string;
  isStreaming?: boolean;
  /** Required when streaming so live tool segments with proposal payloads
   *  can render their inline cards (passes through to SegmentRenderer). */
  threadId?: string;
  /**
   * Stop reason from the last finalized stream, surfaced so the live bubble
   * can show a "Stopped" indicator immediately on user-initiated aborts
   * (before the thread refetch lands with the persisted chat_messages row).
   */
  lastStopReason?: string | null;
  onArtifactClick?: (artifact: ArtifactInfo) => void;
  onToolCallClick?: (toolCallId: string) => void;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  isTyping = false,
  liveSegments = [],
  thinkingText,
  isStreaming = false,
  threadId,
  lastStopReason,
  onArtifactClick,
  onToolCallClick,
}) => {
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [thinkingExpanded, setThinkingExpanded] = useState(true);

  const hasThinking = !!thinkingText && thinkingText.length > 0;
  const hasSegments = liveSegments.length > 0;
  const hasAnyText = liveSegments.some(s => s.type === 'text' && s.text.length > 0);

  useEffect(() => {
    if (hasAnyText) {
      setThinkingExpanded(false);
    } else if (hasThinking && !hasAnyText) {
      setThinkingExpanded(true);
    }
  }, [hasThinking, hasAnyText]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, liveSegments, thinkingText, isStreaming]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center mx-auto mb-4">
            <Bot className="w-8 h-8 text-primary-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            Start a conversation
          </h3>
          <p className="text-sm text-muted-foreground">
            Ask questions about your business data, get insights, and explore anomalies.
            The AI will query your data and remember important context.
          </p>
        </div>
      </div>
    );
  }

  const showStreamingBubble = isStreaming || hasSegments;

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
    >
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onArtifactClick={onArtifactClick} onToolCallClick={onToolCallClick} />
      ))}

      {/* Streaming response */}
      {showStreamingBubble && (
        <div className="flex gap-3 max-w-[85%] mr-auto">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center">
            <Bot className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="flex flex-col gap-1.5 items-start w-full">
            {/* Live thinking block */}
            {hasThinking && (
              <div className="w-full rounded-xl border border-primary/40 bg-accent/50 overflow-hidden">
                <button
                  onClick={() => setThinkingExpanded(prev => !prev)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-primary hover:bg-accent/50 transition-colors"
                >
                  {thinkingExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                  <Sparkles className="w-3.5 h-3.5" />
                  {isStreaming && !hasAnyText ? (
                    <span className="flex items-center gap-1.5">
                      Thinking
                      <Loader2 className="w-3 h-3 animate-spin" />
                    </span>
                  ) : (
                    <span>Thinking</span>
                  )}
                </button>
                {thinkingExpanded && (
                  <div className="px-3 pb-2.5 max-h-64 overflow-y-auto">
                    <p className="text-xs text-primary/80 dark:text-primary/70 whitespace-pre-wrap break-words leading-relaxed font-mono">
                      {thinkingText}
                      {isStreaming && !hasAnyText && (
                        <span className="inline-block w-1 h-3 ml-0.5 bg-primary animate-pulse rounded-sm" />
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Interleaved segments (text + tool pills) */}
            {hasSegments ? (
              <>
                <div className="px-4 py-2.5 rounded-2xl rounded-bl-md bg-muted text-foreground">
                  <SegmentRenderer segments={liveSegments} isStreaming={isStreaming} chatThreadId={threadId} onToolCallClick={onToolCallClick} />
                </div>
                {!isStreaming && lastStopReason === 'aborted' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-muted-foreground bg-muted border border-border">
                    <CircleStop className="w-3 h-3" />
                    Stopped
                  </span>
                )}
              </>
            ) : isStreaming && !hasThinking ? (
              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-muted">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span>Thinking...</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Legacy typing indicator (for non-streaming fallback) */}
      {isTyping && !isStreaming && (
        <div className="flex gap-3 max-w-[85%] mr-auto">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center">
            <Bot className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-muted">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
};

export default MessageList;
